import React, { useEffect, useState } from 'react';
import { streamsService, LearningStream } from '../services/streams';
import { logsService, DailyLog } from '../services/logs';
import { papersService, ResearchPaper } from '../services/papers';
import { projectsService, Project } from '../services/projects';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from 'recharts';

const Analytics: React.FC = () => {
  const [streams, setStreams] = useState<LearningStream[]>([]);
  const [logs, setLogs] = useState<DailyLog[]>([]);
  const [papers, setPapers] = useState<ResearchPaper[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalyticsData();
  }, []);

  const loadAnalyticsData = async () => {
    try {
      const [streamsData, logsData, papersData, projectsData] = await Promise.all([
        streamsService.getAll(),
        logsService.getAll(),
        papersService.getAll(),
        projectsService.getAll(),
      ]);
      setStreams(streamsData);
      setLogs(logsData);
      setPapers(papersData);
      setProjects(projectsData);
    } catch (error) {
      console.error('Error loading analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCumulativeHoursData = () => {
    const sortedLogs = [...logs].sort((a, b) => 
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );
    let cumulative = 0;
    return sortedLogs.map(log => {
      cumulative += log.hours_studied;
      return {
        date: new Date(log.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        hours: cumulative,
        daily: log.hours_studied,
      };
    });
  };

  const getMonthlyHoursData = () => {
    const monthlyData: Record<string, number> = {};
    logs.forEach(log => {
      const date = new Date(log.date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      monthlyData[monthKey] = (monthlyData[monthKey] || 0) + log.hours_studied;
    });
    return Object.entries(monthlyData)
      .sort()
      .map(([month, hours]) => ({
        month: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        hours: Math.round(hours),
      }));
  };

  const getStreamProgressData = () => {
    return streams.map(stream => ({
      name: stream.name,
      progress: Math.round((stream.completed_hours / stream.target_hours) * 100),
      hours: stream.completed_hours,
      target: stream.target_hours,
    }));
  };

  const getSkillRadarData = () => {
    // Calculate average confidence per stream (simplified)
    const streamConfidences: Record<string, number> = {};
    streams.forEach(stream => {
      const streamLogs = logs.filter(log => log.stream_id === stream.id);
      if (streamLogs.length > 0) {
        const avgConfidence = streamLogs.reduce((sum, log) => sum + log.confidence_score, 0) / streamLogs.length;
        streamConfidences[stream.name] = Math.round(avgConfidence);
      }
    });
    
    return [
      {
        subject: 'German',
        score: streamConfidences['German'] || 0,
        fullMark: 10,
      },
      {
        subject: 'ML/AI',
        score: streamConfidences['ML_AI'] || 0,
        fullMark: 10,
      },
      {
        subject: 'Research',
        score: streamConfidences['Research'] || 0,
        fullMark: 10,
      },
      {
        subject: 'Backend',
        score: streamConfidences['Backend'] || 0,
        fullMark: 10,
      },
    ];
  };

  const getPaperReadingVelocity = () => {
    const monthlyPapers: Record<string, number> = {};
    papers.filter(p => p.status === 'Complete').forEach(paper => {
      if (paper.reading_end_date) {
        const date = new Date(paper.reading_end_date);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        monthlyPapers[monthKey] = (monthlyPapers[monthKey] || 0) + 1;
      }
    });
    return Object.entries(monthlyPapers)
      .sort()
      .map(([month, count]) => ({
        month: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short' }),
        papers: count,
      }));
  };

  if (loading) {
    return <div className="p-6">Loading analytics...</div>;
  }

  const totalHours = logs.reduce((sum, log) => sum + log.hours_studied, 0);
  const totalPapers = papers.filter(p => p.status === 'Complete').length;
  const completedProjects = projects.filter(p => p.status === 'Complete' || p.status === 'Published').length;
  const avgConfidence = logs.length > 0
    ? (logs.reduce((sum, log) => sum + log.confidence_score, 0) / logs.length).toFixed(1)
    : '0';

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Analytics & Insights
          </h1>
          <p className="text-gray-600">
            Deep dive into your learning patterns and progress
          </p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <p className="text-sm text-gray-600 mb-1">Total Hours Logged</p>
            <p className="text-3xl font-bold text-primary">{totalHours.toFixed(1)}</p>
            <p className="text-xs text-gray-500 mt-1">Across all streams</p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6">
            <p className="text-sm text-gray-600 mb-1">Papers Read</p>
            <p className="text-3xl font-bold text-accent">{totalPapers}</p>
            <p className="text-xs text-gray-500 mt-1">Goal: 100 by Aug 2027</p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6">
            <p className="text-sm text-gray-600 mb-1">Projects Completed</p>
            <p className="text-3xl font-bold text-secondary">{completedProjects}</p>
            <p className="text-xs text-gray-500 mt-1">Goal: 8-10 projects</p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6">
            <p className="text-sm text-gray-600 mb-1">Avg Confidence</p>
            <p className="text-3xl font-bold text-green-600">{avgConfidence}/10</p>
            <p className="text-xs text-gray-500 mt-1">Across all activities</p>
          </div>
        </div>

        {/* Cumulative Hours Trend */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h3 className="text-xl font-semibold mb-4">Cumulative Hours Trend</h3>
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart data={getCumulativeHoursData()}>
              <defs>
                <linearGradient id="colorHours" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Area
                type="monotone"
                dataKey="hours"
                stroke="#3b82f6"
                fillOpacity={1}
                fill="url(#colorHours)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Monthly Hours */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h3 className="text-xl font-semibold mb-4">Monthly Study Hours</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={getMonthlyHoursData()}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="hours" fill="#22c55e" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Stream Progress Comparison */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h3 className="text-xl font-semibold mb-4">Stream Progress Comparison</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={getStreamProgressData()}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="progress" fill="#a855f7" name="Progress %" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Skill Radar Chart */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h3 className="text-xl font-semibold mb-4">Skill Confidence Radar</h3>
          <ResponsiveContainer width="100%" height={400}>
            <RadarChart data={getSkillRadarData()}>
              <PolarGrid />
              <PolarAngleAxis dataKey="subject" />
              <PolarRadiusAxis angle={90} domain={[0, 10]} />
              <Radar
                name="Confidence"
                dataKey="score"
                stroke="#3b82f6"
                fill="#3b82f6"
                fillOpacity={0.6}
              />
              <Tooltip />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        {/* Paper Reading Velocity */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h3 className="text-xl font-semibold mb-4">Paper Reading Velocity</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={getPaperReadingVelocity()}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="papers" stroke="#f59e0b" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Learning Consistency */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-semibold mb-4">Learning Consistency</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold">{logs.length}</p>
              <p className="text-sm text-gray-600">Total Logs</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold">
                {new Set(logs.map(log => log.date)).size}
              </p>
              <p className="text-sm text-gray-600">Days Logged</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold">
                {logs.length > 0 ? (totalHours / new Set(logs.map(log => log.date)).size).toFixed(1) : '0'}
              </p>
              <p className="text-sm text-gray-600">Avg Hours/Day</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;

